import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {ANALYSIS_TOOL_SPACING} from 'analysis-tool/AnalysisToolConstants';
import {assert} from 'Asserts';
import {SystemColumnName} from 'data-frames/DataFrameConstants';
import {DataFrame, DataFrameRow, DataFrameColumnDef, DataType} from 'data-frames/DataFrameTypes';
import {createAccessors} from 'data-frames/util/createAccessors';
import isSystemColumn from 'data-frames/util/isSystemColumn';
import resolveDataFrame from 'data-frames/util/resolveDataFrame';
import sampleRows from 'data-frames/util/sampleRows';
import sortDataFrame from 'data-frames/util/sortDataFrame';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {
  AnalyticsVisualizationLegendLocation as LegendLocation,
  AnalyticsVisualizationAxisScale,
  AnalyticsVisualizationColorPalette,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {VISUALIZATION_RESOLUTION_CONFIG} from 'insights/analytics-visualizations/AnalyticsVisualizationConstants';
import convertScatterPlotProps from 'insights/analytics-visualizations/viewers/prop-converters/convertScatterPlotProps';
import makeVisualizationConfig from 'insights/universal-blocks/makeVisualizationConfig';
import {VisualizationType} from 'insights/universal-blocks/SharedUniversalBlockViewTypes';
import {ParameterOptimizationPredictionResponse} from 'machine-learning/MachineLearningDAO';
import {
  ALL_DATA_COLUMNS_MAPPING,
  ALL_DATA_VALIDATION_MAP,
} from 'machine-learning/parameter-optimization/legacy-components/ParameterOptimizationConstants';
import ScatterPlot, {ScatterPlotData} from 'ui-library/charting/ScatterPlot';
import {areLabeledDatasets} from 'ui-library/charting/Types';
import Pane from 'ui-library/pane/Pane';
import {omit} from 'util/omitKey';
import pick from 'util/pick';

/** @package */
export const OUTPUT_COLUMN_DEF: DataFrameColumnDef = {
  displayName: 'Output',
  systemName: 'output',
  type: {leafType: LeafType.DECIMAL},
};
/** @package */
export const OUTPUT_TYPE_COLUMN_DEF: DataFrameColumnDef = {
  displayName: 'Output type',
  systemName: 'output_type',
  type: {leafType: LeafType.TEXT},
};
/** @package */
export const RANK_COLUMN_DEF: DataFrameColumnDef = {
  displayName: 'Rank',
  systemName: 'rank',
  type: {leafType: LeafType.INTEGER},
};
/** @package */
export const MAX_PREDICTIONS_TO_SHOW = 500;
const ERROR_VALUE_LABEL = 'Prediction error';

const PARAMETERS = {
  aggregationType: null,
  colorPalette: AnalyticsVisualizationColorPalette.DISCRETE,
  errorBarType: null,
  legendLocation: LegendLocation.TOP,
  outlierConfig: null,
  regressionConfig: null,
  rowIdColumnName: null,
  seriesColumnName: OUTPUT_TYPE_COLUMN_DEF.systemName,
  xAxisColumnName: RANK_COLUMN_DEF.systemName,
  xAxisScale: AnalyticsVisualizationAxisScale.LINEAR,
  yAxisColumnName: OUTPUT_COLUMN_DEF.systemName,
  yAxisIncludesZero: true,
  yAxisMin: null,
  yAxisMax: null,
  yAxisScale: AnalyticsVisualizationAxisScale.LINEAR,
};

export const PREDICTIONS_SCATTER_PLOT_NAME = 'Ranked outputs';
export const PREDICTIONS_SCATTER_PLOT_DESCRIPTION =
  'This chart shows all actual and predicted outputs from your experimental data. If you have submitted a prediction, it will appear as the leftmost point.';

/**
 * Given an `AllData` data frame, transform the data frame such that the columns `actual_output`
 * and `predicted_output` of each row are transformed into two rows with columns `output` (the value of either
 * `actual_output` or `predicted_output`) and `outputType` (either `'actual'` or `'predicted'` depending on the
 * source column for the value of `output`). Also adds a rank column that is the index of the points in the
 * sorted order. Finally, the column definitions of the data frame are updated to reflect the transformation.
 *
 * This transformation allows us to plot actual and predicted values as separate series on the y-axis with
 * the points sorted by descending actual output.
 *
 * @package
 */
export function transformAllDataDataFrame(allData: DataFrame): DataFrame {
  const {accessors} = createAccessors(
    allData,
    pick(ALL_DATA_COLUMNS_MAPPING, 'predictedOutput', 'actualOutput'),
    pick(ALL_DATA_VALIDATION_MAP, 'predictedOutput', 'actualOutput')
  );
  const sampledDataFrame = sampleRows(allData, MAX_PREDICTIONS_TO_SHOW);
  const sortedDataFrame = sortDataFrame(sampledDataFrame, {
    columnKey: ALL_DATA_COLUMNS_MAPPING.actualOutput,
    sortDirection: 'arrow-down',
  });

  const rows: Array<DataFrameRow> = sortedDataFrame.rows.flatMap((row, index) => {
    const filteredRow = omit(
      row,
      ALL_DATA_COLUMNS_MAPPING.actualOutput,
      ALL_DATA_COLUMNS_MAPPING.predictedOutput
    );
    // Add 1 so that the prediction output point can have rank 0
    const rank = index + 1;

    return [
      {
        ...filteredRow,
        [SystemColumnName.ROW_ID]: `${accessors.rowId(row)}_actual`,
        output: accessors.actualOutput(row),
        output_type: 'actual',
        rank,
      },
      {
        ...filteredRow,
        [SystemColumnName.ROW_ID]: `${accessors.rowId(row)}_predicted`,
        output: accessors.predictedOutput(row),
        output_type: 'predicted',
        rank,
      },
    ];
  });

  return {
    ...allData,
    columns: [
      RANK_COLUMN_DEF,
      OUTPUT_COLUMN_DEF,
      OUTPUT_TYPE_COLUMN_DEF,
      ...allData.columns.filter(
        (col) =>
          col.systemName === 'actual_output' || col.systemName === 'predicted_output' || isSystemColumn(col)
      ),
    ],
    rows,
  };
}

/**
 * Given a ScatterPlotData and a prediction output, this function appends a scatter plot point to the data
 * that reflects the output of a prediction that was generated by the user. This prediction does not
 * correspond to an actual result, so there is no point added to the 'actual' partition.
 *
 * @package
 */
export function addPredictionPointToScatterPlotData(
  data: ScatterPlotData,
  predictionOutput: ParameterOptimizationPredictionResponse
): ScatterPlotData {
  assert(areLabeledDatasets(data.datasets), 'Expected labeled datasets');
  const predictionDataset = data.datasets.find((dataset) => dataset.label === 'predicted');
  assert(predictionDataset != null, 'Prediction data frame was incorrectly converted to legacy data frame');
  const errorValue = predictionOutput.upperBound - predictionOutput.lowerBound;
  const datasetWithError = {
    ...predictionDataset,
    points: [...predictionDataset.points, {x: 0, y: predictionOutput.value, errorValue}],
  };

  return {
    axisTypes: {
      ...data.axisTypes,
      errorValue: DataType.NUMBER,
    },
    datasets: [data.datasets[0], datasetWithError],
  };
}

interface PredictionsScatterPlotProps {
  allData: DataFrame;
  predictionOutput: ParameterOptimizationPredictionResponse | null;
}

function PredictionsScatterPlot({allData, predictionOutput}: PredictionsScatterPlotProps): JSX.Element {
  const parameters = makeVisualizationConfig(VisualizationType.SCATTER_PLOT, PARAMETERS).parameters;

  // We manually convert the dataFrame and directly render the ScatterPlot here in order to be able to
  // attach an `errorValue` to the point that we append for the generated prediction.
  const predictionsVsActualDataFrame = transformAllDataDataFrame(allData);
  const resolvedDataFrame = resolveDataFrame(predictionsVsActualDataFrame, VISUALIZATION_RESOLUTION_CONFIG);
  const conversionOutput = convertScatterPlotProps(resolvedDataFrame, parameters);

  assert(conversionOutput.valid, 'Unexpected malformed dataframe');

  const {data, config} = conversionOutput.props;
  const finalData: ScatterPlotData =
    predictionOutput != null ? addPredictionPointToScatterPlotData(data, predictionOutput) : data;
  const finalConfig = {
    ...config,
    errorBarConfig: {errorValueLabel: ERROR_VALUE_LABEL},
  };

  return (
    <Pane
      className={css(styles.pane)}
      title={PREDICTIONS_SCATTER_PLOT_NAME}
      description={PREDICTIONS_SCATTER_PLOT_DESCRIPTION}
      centerContent={false}
    >
      <div className={classNames('u-flex-full-height', css(styles.chartContainer))}>
        <ScatterPlot data={finalData} config={finalConfig} />
      </div>
    </Pane>
  );
}

export default React.memo(PredictionsScatterPlot);

const styles = StyleSheet.create({
  pane: {
    padding: ANALYSIS_TOOL_SPACING,
  },
  chartContainer: {
    // without this padding, the scatter plot becomes slightly too wide and causes a horizontal scrollbar
    padding: 5,
  },
});
